import {
  reconcileTopAppsToTotal,
  UNATTRIBUTED_APP_IDENTIFIER,
  UNATTRIBUTED_APP_NAME,
} from "./usageTotalReconciliation";

export { UNATTRIBUTED_APP_IDENTIFIER, UNATTRIBUTED_APP_NAME };

export type MergeableApp = {
  appIdentifier?: string;
  displayName: string;
  durationSeconds: number;
  category?: string;
  isEstimated?: boolean;
  color?: string;
  opens?: number;
  hourly?: Array<{ hour: number; seconds: number; opens?: number }>;
};

export type MergeableTimeBand = {
  key: "morning" | "afternoon" | "evening" | "night";
  seconds: number;
  opens?: number;
};

export function appKey(app: Pick<MergeableApp, "appIdentifier" | "displayName">): string {
  return app.appIdentifier ?? app.displayName;
}

export function isUnattributedApp(app: Pick<MergeableApp, "appIdentifier" | "displayName">): boolean {
  return app.appIdentifier === UNATTRIBUTED_APP_IDENTIFIER || app.displayName === UNATTRIBUTED_APP_NAME;
}

function unattributedSeconds(apps: MergeableApp[]): number {
  return apps.find(isUnattributedApp)?.durationSeconds ?? 0;
}

function pickRicherApp(existing?: MergeableApp, incoming?: MergeableApp): MergeableApp | undefined {
  if (!existing) return incoming;
  if (!incoming) return existing;
  return incoming.durationSeconds >= existing.durationSeconds ? incoming : existing;
}

/**
 * Merges per-app rows for a same-calendar-day iOS resync. After reinstall, native
 * Screen Time often reports pre-uninstall usage as "Other" while Convex may still
 * hold the last good attribution from an earlier sync today.
 */
export function mergeSameDayIosTopApps(existingApps: MergeableApp[], incomingApps: MergeableApp[]): MergeableApp[] {
  let orphanPool = unattributedSeconds(incomingApps);

  const existingByKey = new Map<string, MergeableApp>();
  for (const app of existingApps) {
    if (!isUnattributedApp(app)) existingByKey.set(appKey(app), app);
  }

  const incomingByKey = new Map<string, MergeableApp>();
  for (const app of incomingApps) {
    if (!isUnattributedApp(app)) incomingByKey.set(appKey(app), app);
  }

  const merged: MergeableApp[] = [];
  for (const key of new Set([...existingByKey.keys(), ...incomingByKey.keys()])) {
    const existing = existingByKey.get(key);
    const incoming = incomingByKey.get(key);
    const existingDur = existing?.durationSeconds ?? 0;
    const incomingDur = incoming?.durationSeconds ?? 0;

    let duration = Math.max(existingDur, incomingDur);
    let template = pickRicherApp(existing, incoming) ?? existing ?? incoming!;

    if (existingDur > incomingDur && orphanPool > 0) {
      const recovery = Math.min(existingDur - incomingDur, orphanPool);
      duration = Math.max(duration, incomingDur + recovery);
      orphanPool -= recovery;
      template = existing ?? template;
    }

    const opens = Math.max(existing?.opens ?? 0, incoming?.opens ?? 0);
    merged.push({
      ...template,
      durationSeconds: duration,
      opens: opens > 0 ? opens : undefined,
      hourly: pickRicherApp(existing, incoming)?.hourly,
      category: existing?.category ?? incoming?.category,
      color: incoming?.color ?? existing?.color,
    });
  }

  return merged;
}

/** Reconcile merged app rows with the authoritative iOS segment total. */
export function finalizeSameDayTopApps(apps: MergeableApp[], authoritativeTotalSeconds: number): MergeableApp[] {
  return reconcileTopAppsToTotal(apps, authoritativeTotalSeconds);
}

const TIME_BAND_KEYS: readonly MergeableTimeBand["key"][] = ["morning", "afternoon", "evening", "night"];

export function mergeSameDayTimeBands(
  existingBands: MergeableTimeBand[],
  incomingBands: MergeableTimeBand[],
): MergeableTimeBand[] {
  const existingByKey = new Map(existingBands.map((band) => [band.key, band]));
  const incomingByKey = new Map(incomingBands.map((band) => [band.key, band]));

  return TIME_BAND_KEYS.map((key) => {
    const existing = existingByKey.get(key);
    const incoming = incomingByKey.get(key);
    const seconds = Math.max(existing?.seconds ?? 0, incoming?.seconds ?? 0);
    const opens = Math.max(existing?.opens ?? 0, incoming?.opens ?? 0);
    return {
      key,
      seconds,
      opens: opens > 0 ? opens : undefined,
    };
  });
}

export function mergeSameDayIosSnapshot(
  existing: {
    topApps: MergeableApp[];
    totalScreenTimeSeconds?: number;
    timeBands: MergeableTimeBand[];
  },
  incoming: {
    topApps: MergeableApp[];
    totalSeconds: number;
    timeBands: MergeableTimeBand[];
  },
) {
  // Same-calendar-day screen time is cumulative, so a later capture can never
  // legitimately report less than an earlier one. DeviceActivity occasionally
  // returns a partial/low segment total; never let it clobber a higher stored total.
  const authoritativeTotal = Math.max(incoming.totalSeconds, existing.totalScreenTimeSeconds ?? 0);
  const mergedTopApps = finalizeSameDayTopApps(
    mergeSameDayIosTopApps(existing.topApps, incoming.topApps),
    authoritativeTotal,
  );

  return {
    topApps: mergedTopApps,
    totalScreenTimeSeconds: authoritativeTotal,
    timeBands: mergeSameDayTimeBands(existing.timeBands, incoming.timeBands),
  };
}

/**
 * Merge for a completed past calendar day re-fetched from native (history
 * backfill). A completed day's usage is fixed, so a genuine full-day
 * re-capture can only report per-app values >= the frozen partial rows stored
 * during that day — the accumulate-and-reconcile merge accepts those upgrades.
 * After a Screen0 reinstall, though, backfill re-fetches days whose usage iOS
 * can no longer attribute (it arrives as "Other"); replacing stored rows with
 * that capture would re-attribute known apps to "Other", so orphaned incoming
 * usage is recovered back to the apps Convex already attributed instead.
 */
export function mergePastDayIosSnapshot(
  existing: {
    topApps: MergeableApp[];
    totalScreenTimeSeconds?: number;
    timeBands: MergeableTimeBand[];
  },
  incoming: {
    topApps: MergeableApp[];
    totalSeconds: number;
    timeBands: MergeableTimeBand[];
  },
) {
  return mergeSameDayIosSnapshot(existing, incoming);
}

/**
 * Android merge, used for both same-day resyncs and past-day backfill. Unlike
 * iOS there is no authoritative segment total — an Android total is by
 * definition the sum of its per-app foreground rows — so after the per-app
 * max-union the total is lifted to the merged app sum (never below either
 * stored total). Reinstalls keep attribution on Android (usage lives in the
 * OS, not the app), so the merge's job is only to keep stored rows from being
 * clobbered by a partial re-capture (events pruned by retention or cleared by
 * a reboot, or the aggregates-only fallback missing an app).
 */
export function mergeAndroidDaySnapshot(
  existing: {
    topApps: MergeableApp[];
    totalScreenTimeSeconds?: number;
    timeBands: MergeableTimeBand[];
  },
  incoming: {
    topApps: MergeableApp[];
    totalSeconds: number;
    timeBands: MergeableTimeBand[];
  },
) {
  const mergedTopApps = mergeSameDayIosTopApps(existing.topApps, incoming.topApps);
  const mergedAppSum = mergedTopApps
    .filter((app) => !isUnattributedApp(app))
    .reduce((sum, app) => sum + app.durationSeconds, 0);
  const totalScreenTimeSeconds = Math.max(
    incoming.totalSeconds,
    existing.totalScreenTimeSeconds ?? 0,
    mergedAppSum,
  );

  return {
    topApps: reconcileTopAppsToTotal(mergedTopApps, totalScreenTimeSeconds),
    totalScreenTimeSeconds,
    timeBands: mergeSameDayTimeBands(existing.timeBands, incoming.timeBands),
  };
}
